// Achievements on a profile and on You: the Phoenix's crown over the badge grid (tap either for its
// category), the category sheet listing every achievement in it, and the picker for the badge beside
// your name. Every name and requirement comes from the API, which keeps a secret to itself until the
// viewer has earned it.
import { useState, type CSSProperties, type ReactNode } from 'react'
import { ACHIEVEMENT_CATEGORIES, BADGE_TIERS, SHOWABLE_TIERS, tierFor, type AchievementCategory, type AchievementItem, type BadgeCategory, type BadgeTier, type MyAchievements } from '@wbwl/contract'
import { ApiFailure, api } from '../lib/api.ts'
import { fmt } from '../lib/game.ts'
import { invalidate, setResource } from '../lib/store.ts'
import { Badge } from './Badge.tsx'
import { BADGE_TIER_NAMES } from './badges.ts'
import { Sheet, useToast } from './shell.tsx'

/** Short enough for a grid cell. */
export const SHORT_NAME: Record<BadgeCategory, string> = {
  cub: 'Cub Reporter', regular: 'The Regular', archivist: 'Archivist', headliner: 'Headliner',
  wordsmith: 'Wordsmith', firekeeper: 'Firekeeper', rival: 'The Rival', legend: 'Legend', phoenix: 'The Phoenix',
}
/** Each tier's colour, for rings, bars and ticks. */
export const TIER_COLOR: Record<BadgeTier | 'locked', string> = { locked: 'var(--rule-2)', bronze: '#C98A4E', silver: '#D5DAE0', gold: '#E8B83A', blaze: '#FF7A2A' }
export const earnedOn = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
const showable = (t: BadgeTier | null) => t !== null && SHOWABLE_TIERS.includes(t)

/** How far the Phoenix has rekindled: the share of the eight categories set Ablaze. */
const litOf = (c: AchievementCategory) => c.earned / c.total
/** When it rose: the last of its eight. */
const risenOn = (c: AchievementCategory) => c.items.reduce((at, i) => (i.earnedAt && i.earnedAt > at ? i.earnedAt : at), '')

/**
 * The Phoenix, above the grid: the badge above all the others, in its ashes until a category goes
 * Ablaze, rekindling from the tail with each, risen at eight. Never secret, so it's always there to chase.
 */
export function PhoenixCrown({ category: c, shown, onOpen }: { category: AchievementCategory; shown: boolean; onOpen(): void }) {
  const risen = c.earned >= c.total
  return (
    <button type="button" className={`ach-crown${risen ? ' risen' : ''}${shown ? ' shown' : ''}`} onClick={onOpen}
      aria-label={`The Phoenix, ${risen ? 'risen' : `${c.earned} of ${c.total} categories Ablaze`}${shown ? ', shown beside the name' : ''}`}>
      <Badge category="phoenix" tier={c.tier ?? 'locked'} lit={litOf(c)} size={92} />
      <span className="txt">
        <span className="over">The badge above all</span>
        <b>The Phoenix</b>
        <span className="ct">{risen ? `Risen · ${earnedOn(risenOn(c))}` : `${c.earned} of ${c.total} Ablaze`}</span>
        <Bar8 lit={c.earned} />
      </span>
      <span className="chev" aria-hidden="true">›</span>
    </button>
  )
}

/** Eight segments, filling from the left: one for each category Ablaze, whichever they are. */
function Bar8({ lit }: { lit: number }) {
  return <span className="bar8" aria-hidden="true">{Array.from({ length: 8 }, (_, i) => <i key={i} className={i < lit ? 'on' : ''} />)}</span>
}

/** The eight category badges with how many of each are earned; the one beside the name carries a check. */
export function BadgeGrid({ categories, shown, onOpen }: { categories: readonly AchievementCategory[]; shown: BadgeCategory | null; onOpen(id: BadgeCategory): void }) {
  return (
    <div className="card ach-grid">
      {categories.filter((c) => c.id !== 'phoenix').map((c) => (
        <button type="button" key={c.id} className={`ach-cell${c.id === shown ? ' shown' : ''}`} onClick={() => onOpen(c.id)}
          aria-label={`${c.name}, ${c.earned} of ${c.total}${c.id === shown ? ', shown beside the name' : ''}`}>
          <Badge category={c.id} tier={c.tier ?? 'locked'} size={58} />
          <b>{SHORT_NAME[c.id]}</b>
          <span>{c.earned} of {c.total}</span>
        </button>
      ))}
    </div>
  )
}

/**
 * One category, opened from a badge: the count, a bar with where each tier begins, and every
 * achievement in it. On your own: progress toward counts. On someone else's: when they earned
 * each, your own count, and "you too" on the ones you share.
 */
export function CategorySheet({ category, all, owner, onClose }: {
  category: AchievementCategory | null
  /** Every category on the list, so the Phoenix's rows can show each category's badge. */
  all?: readonly AchievementCategory[]
  owner: string | null
  onClose(): void
}) {
  return (
    <Sheet open={category !== null} onClose={onClose} label={category ? `${category.name} achievements` : 'Achievements'} className="pinned">
      {category && <CategoryBody c={category} all={all ?? []} owner={owner} />}
    </Sheet>
  )
}

function CategoryBody({ c, all, owner }: { c: AchievementCategory; all: readonly AchievementCategory[]; owner: string | null }) {
  const own = owner === null
  const tier = c.tier ?? 'locked'
  const phoenix = c.id === 'phoenix'
  const nextAt = c.steps.findIndex((s) => s > c.earned)
  const viewer = own ? null : c.items.filter((i) => i.mine).length
  const left = phoenix
    ? (c.earned < c.total ? `${c.total - c.earned} to go.` : 'Risen.')
    : nextAt >= 0 ? `${c.steps[nextAt] - c.earned} more to ${BADGE_TIER_NAMES[BADGE_TIERS[nextAt]]}.` : 'Every one earned.'
  // The Phoenix's rows are the categories: each shows that category's badge as it stands.
  const marker = (it: AchievementItem) => {
    const of = phoenix ? all.find((x) => it.key === `ablaze-${x.id}`) : undefined
    return of ? <Badge category={of.id} tier={of.tier ?? 'locked'} size={26} simple /> : undefined
  }
  return (
    <div className="cat-sheet" style={{ '--tier': TIER_COLOR[phoenix ? 'blaze' : tier] } as CSSProperties}>
      <div className="cat-head">
        <Badge category={c.id} tier={tier} lit={phoenix ? litOf(c) : undefined} size={phoenix ? 78 : 64} />
        <div className="ctxt"><h3>{c.name}</h3><span className="ct">{c.earned} of {c.total} <span>· {own ? 'yours' : owner}</span></span></div>
      </div>
      <p className="cline">{ACHIEVEMENT_CATEGORIES[c.id].line} {left}</p>
      <div className="tbar" style={{ gridTemplateColumns: `repeat(${c.total}, 1fr)` }} aria-hidden="true">
        {c.items.map((_, i) => <i key={i} className={i < c.earned ? 'on' : ''} />)}
      </div>
      <div className="tlbl" aria-hidden="true">
        {phoenix
          ? <span className={c.earned >= c.total ? 'hit' : ''} style={{ left: '100%' }}>Risen at {c.total}</span>
          : c.steps.map((s, i) => <span key={s} className={c.earned >= s ? 'hit' : ''} style={{ left: `${(100 * s) / c.total}%` }}>{BADGE_TIER_NAMES[BADGE_TIERS[i]]} {s}</span>)}
      </div>
      {viewer !== null && (
        <div className="vs-line"><Badge category={c.id} tier={tierFor(c.id, viewer) ?? 'locked'} lit={phoenix ? viewer / c.total : undefined} size={20} /><span>You: <b>{viewer} of {c.total}</b></span></div>
      )}
      {/* The head stays put; only the list scrolls. */}
      <div className="alist sheet-scroll">{c.items.map((it, i) => <ItemRow key={it.key === 'secret' ? `secret-${i}` : it.key} it={it} marker={marker(it)} />)}</div>
    </div>
  )
}

function ItemRow({ it, marker }: { it: AchievementItem; marker?: ReactNode }) {
  const done = it.earnedAt !== null
  // A secret the viewer hasn't found: only that it's there (and, on someone else's list, when they found it).
  if (it.key === 'secret') {
    return (
      <div className={`ar secret ${done ? 'done' : 'todo'}`}>
        <span className="mk">{done ? '✓' : '?'}</span>
        <b>Secret</b>
        <span className="rq">{done ? 'Find it yourself to see what it is.' : 'Keep playing to find it.'}</span>
        <span className="rt">{done && <span>{earnedOn(it.earnedAt!)}</span>}</span>
      </div>
    )
  }
  return (
    <div className={`ar ${done ? 'done' : 'todo'}${marker ? ' badged' : ''}`}>
      {marker ?? <span className="mk">{done ? '✓' : ''}</span>}
      <b>{it.name}{it.secret && <span className="sec2">secret</span>}</b>
      <span className="rq">{it.requirement}</span>
      <span className="rt">
        {done
          ? <><span>{earnedOn(it.earnedAt!)}</span>{it.mine && <span className="you">you too</span>}</>
          : it.progress && <span className="pg">{fmt(it.progress.have)}/{fmt(it.progress.need)}<i><b style={{ width: `${(100 * it.progress.have) / it.progress.need}%` }} /></i></span>}
      </span>
    </div>
  )
}

/**
 * Beside your name: Silver and up, one at a time, rarest first, with the Phoenix ahead of them all
 * once it has risen. Left alone it's the Phoenix or your rarest; you can pick one, go back to the
 * default, or show none.
 */
/** After the badge beside your name changes: your achievements as the API now has them, and every list that shows your name with its badge. */
export function badgeChanged(data: MyAchievements) {
  setResource('achievements', data)
  invalidate('me', 'board:', 'rivals', 'rivalry:', 'profile:', 'challenge:', 'edchallenges:', 'mychallenges')
}

export function BadgePicker({ open, onClose, data }: { open: boolean; onClose(): void; data: MyAchievements }) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  // Rarest first, then the higher tier: the order the default picks in.
  const rank = (t: BadgeTier | null) => (t ? BADGE_TIERS.indexOf(t) : -1)
  const first = (c: AchievementCategory) => Number(c.id === 'phoenix' && showable(c.tier))
  const opts = [...data.categories].sort((a, b) => first(b) - first(a) || Number(showable(b.tier)) - Number(showable(a.tier)) || data.rarity[a.id] - data.rarity[b.id] || rank(b.tier) - rank(a.tier))
  const any = opts.some((c) => showable(c.tier))

  async function pick(category: BadgeCategory | 'none' | null) {
    if (busy) return
    setBusy(true)
    try {
      badgeChanged(await api.put<MyAchievements>('/me/badge', { category }))
      onClose()
    } catch (e) {
      toast(e instanceof ApiFailure && e.body.message ? e.body.message : 'Couldn’t change your badge. Try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet open={open} onClose={onClose} label="Beside your name">
      <div className="pick-sheet">
        <h3 className="h-serif">Beside your name</h3>
        <p className="sub">On leaderboards, Rivals and challenges. Silver and up, or the Phoenix once it has risen; one at a time.</p>
        {!any && <p className="sub faint">Reach Silver in any category and its badge can go here.</p>}
        {opts.map((c) => {
          const ok = showable(c.tier)
          const on = data.choice !== 'none' && data.badge?.category === c.id
          const phoenix = c.id === 'phoenix'
          const tag = on ? (data.choice === null ? (phoenix ? 'Default' : 'Rarest · default') : 'Showing') : ok ? `${data.rarity[c.id]}% have it`
            : phoenix ? 'Rises at 8 of 8' : c.tier === 'bronze' ? 'Silver unlocks it' : 'Locked'
          return (
            <button type="button" key={c.id} className={`opt${on ? ' sel' : ''}${ok ? '' : ' off'}`} disabled={!ok || busy} aria-pressed={on} onClick={() => void pick(c.id)}>
              <span className="rd" />
              <Badge category={c.id} tier={c.tier ?? 'locked'} lit={phoenix ? litOf(c) : undefined} size={30} />
              <div><b>{c.name}</b><span>{phoenix ? (ok ? 'Risen' : `${c.earned} of ${c.total} Ablaze`) : `${BADGE_TIER_NAMES[c.tier ?? 'locked']} · ${c.earned} of ${c.total}`}</span></div>
              <span className="tag">{tag}</span>
            </button>
          )
        })}
        {any && data.choice !== null && (
          <button type="button" className="opt" disabled={busy} onClick={() => void pick(null)}>
            <span className="rd" /><span className="sp" /><div><b>The default</b><span>The Phoenix once it has risen, else your rarest</span></div>
          </button>
        )}
        <button type="button" className={`opt${data.choice === 'none' ? ' sel' : ''}`} disabled={busy} aria-pressed={data.choice === 'none'} onClick={() => void pick('none')}>
          <span className="rd" /><span className="sp" /><div><b>No badge</b><span>Just your name</span></div>
        </button>
      </div>
    </Sheet>
  )
}
